import { useCallback, useEffect, useRef } from 'react'
import type { ChannelApiEvent, ZelloState } from './types'

const NOOP = (_?: unknown) => { /* intentionally empty */ }

// Match the Zello JS SDK reconnect cadence (session.js): start at 5s,
// multiply by 1.5x per attempt, cap at 60s.
const INITIAL_RECONNECT_MS = 5000
const MAX_RECONNECT_MS = 60000
const RECONNECT_BACKOFF = 1.5

// "Stuck logon" watchdog: if Zello accepts our logon but never emits any
// on_channel_status push, the session is stuck (known intermittent issue,
// usually after rapid refresh). Force a fresh logon after this many ms.
// Capped to avoid loops on a permanently broken account/channel.
// Single-shot watchdog: if Zello doesn't push on_channel_status within this
// window after a successful logon, declare the channel(s) failed. We do NOT
// attempt to recover by closing/reopening the WS — the user can refresh
// manually. This trades reliability for predictability (no WS churn,
// stable behavior).
const NO_STATUS_WATCHDOG_MS = 30000

type UseZelloTransportArgs = {
  serverUrl: string
  username?: string
  password?: string
  authToken?: string
  language?: string
  emit: (e: ChannelApiEvent) => void
  setState: React.Dispatch<React.SetStateAction<ZelloState>>
  onJsonMessage: (msg: any) => void
  onBinaryMessage: (data: ArrayBuffer) => void
  onBeforeDisconnect?: () => void
}

type ChannelReadyWaiter = {
  resolve: () => void
  reject: (err: Error) => void
  timer: ReturnType<typeof setTimeout>
}

// Single WebSocket transport for a Zello Work user. Logon subscribes to a
// `channels: [...]` array — Zello Work allows only one concurrent session
// per user, so multi-channel users share one socket.
export function useZelloTransport({
  serverUrl,
  username,
  password,
  authToken,
  language,
  emit,
  setState,
  onJsonMessage,
  onBinaryMessage,
  onBeforeDisconnect,
}: UseZelloTransportArgs) {
  const wsRef = useRef<WebSocket | null>(null)
  const seqRef = useRef(1)
  const isLoggedOnRef = useRef(false)
  const connectPromiseRef = useRef<Promise<void> | null>(null)
  const readyChannelsRef = useRef<Set<string>>(new Set())
  const channelWaitersRef = useRef<Map<string, ChannelReadyWaiter[]>>(new Map())

  // Auto-reconnect bookkeeping (per Zello SDK pattern).
  const refreshTokenRef = useRef<string | null>(null)
  const selfDisconnectRef = useRef(false)
  const lastChannelsRef = useRef<string[]>([])
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const ensureFnRef = useRef<((channels: string[]) => Promise<void>) | null>(null)

  // No-status-after-logon watchdog — single-shot.
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const rejectAllWaiters = useCallback((reason: string) => {
    channelWaitersRef.current.forEach(list => {
      list.forEach(w => {
        clearTimeout(w.timer)
        w.reject(new Error(reason))
      })
    })
    channelWaitersRef.current.clear()
  }, [])

  const waitForChannelReady = useCallback((channel: string, timeoutMs = 5000) => {
    const key = String(channel || '').trim().toLowerCase()
    if (!key) return Promise.reject(new Error('no channel'))
    if (readyChannelsRef.current.has(key)) return Promise.resolve()

    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        const list = channelWaitersRef.current.get(key)
        if (list) {
          const idx = list.indexOf(waiter)
          if (idx >= 0) list.splice(idx, 1)
          if (list.length === 0) channelWaitersRef.current.delete(key)
        }
        reject(new Error('channel not ready timeout'))
      }, timeoutMs)

      const waiter: ChannelReadyWaiter = { resolve, reject, timer }
      const list = channelWaitersRef.current.get(key) || []
      list.push(waiter)
      channelWaitersRef.current.set(key, list)
    })
  }, [])

  const safeSend = useCallback((obj: any) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    try { ws.send(JSON.stringify(obj)); return true } catch (err) { NOOP(err); return false }
  }, [])

  const safeSendBinary = useCallback((buf: ArrayBuffer) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    try { ws.send(buf); return true } catch (err) { NOOP(err); return false }
  }, [])

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const clearWatchdog = useCallback(() => {
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current)
      watchdogTimerRef.current = null
    }
  }, [])

  const scheduleReconnect = useCallback(() => {
    if (!lastChannelsRef.current.length) return
    if (reconnectTimerRef.current) return

    const attempt = reconnectAttemptsRef.current
    const delay = Math.min(
      INITIAL_RECONNECT_MS * Math.pow(RECONNECT_BACKOFF, attempt),
      MAX_RECONNECT_MS,
    )
    reconnectAttemptsRef.current = attempt + 1

    setState(p => ({ ...p, lastEvent: 'reconnect_scheduled' }))
    emit({ type: 'connection_lost' })

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null
      const fn = ensureFnRef.current
      const channels = lastChannelsRef.current
      if (!fn || !channels.length) return

      void fn(channels).catch(err => {
        console.warn('[Zello] reconnect attempt failed', err)
        if (!wsRef.current) scheduleReconnect()
      })
    }, delay)
  }, [emit, setState])

  const closeWs = useCallback(() => {
    selfDisconnectRef.current = true
    clearReconnectTimer()
    clearWatchdog()
    reconnectAttemptsRef.current = 0

    const ws = wsRef.current
    if (!ws) return

    try { ws.onmessage = null } catch (err) { NOOP(err) }
    try { ws.onerror = null } catch (err) { NOOP(err) }
    try { ws.onclose = null } catch (err) { NOOP(err) }

    try { onBeforeDisconnect?.() } catch (err) { NOOP(err) }

    if (ws.readyState === WebSocket.CONNECTING) {
      // Calling close() on a still-CONNECTING WebSocket triggers a browser
      // warning ("WebSocket is closed before the connection is
      // established."). Defer the close until handshake completes — by
      // that time we've nulled out onmessage/onerror/onclose so the
      // ws is effectively orphaned and closes silently.
      try {
        ws.onopen = () => {
          try { ws.close() } catch (e) { NOOP(e) }
        }
      } catch (err) { NOOP(err) }
    } else {
      try { ws.onopen = null } catch (err) { NOOP(err) }
      try { ws.close() } catch (err) { NOOP(err) }
    }
    wsRef.current = null
    isLoggedOnRef.current = false
    seqRef.current = 1
    readyChannelsRef.current.clear()
    rejectAllWaiters('disconnected')

    // NOTE: do NOT clear connectionFailed here — the watchdog's "connection
    // failed" path closes the WS but wants the flag to remain true so the
    // PTT UI keeps showing "Connection failed". It's cleared on logon
    // success and on real on_channel_status arrival instead.
    setState(p => ({
      ...p,
      status: 'disconnected',
      rx: 'idle',
      tx: 'idle',
      readyChannels: [],
    }))
  }, [clearReconnectTimer, clearWatchdog, onBeforeDisconnect, rejectAllWaiters, setState])

  // Single-shot watchdog: if not every subscribed channel has emitted
  // on_channel_status by the deadline, mark connectionFailed and close the
  // WS. closeWs intentionally does NOT clear connectionFailed, so the flag
  // survives the close — UI keeps showing "Connection failed" until the
  // user manually reconnects.
  const armWatchdog = useCallback(() => {
    if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current)

    watchdogTimerRef.current = setTimeout(() => {
      watchdogTimerRef.current = null

      // Defensive: should be unreachable since the on_channel_status
      // handler clears the timer on allReady. But if for some reason the
      // session is fully ready when we fire, don't declare failure.
      const subscribed = lastChannelsRef.current
        .map(c => c.trim().toLowerCase())
        .filter(Boolean)
      const allReady =
        subscribed.length > 0 &&
        subscribed.every(c => readyChannelsRef.current.has(c))
      if (allReady) return

      console.warn('[Zello] not all channels online after logon — declaring connection failed', {
        subscribed,
        ready: Array.from(readyChannelsRef.current),
      })
      setState(p => ({ ...p, connectionFailed: true, lastEvent: 'connection_failed' }))
      closeWs()
    }, NO_STATUS_WATCHDOG_MS)
  }, [closeWs, setState])

  const ensureConnectedAndLoggedOn = useCallback(async (channels: string[]) => {
    if (!username || !password) {
      setState(p => ({ ...p, status: 'failed', error: 'missing username/password' }))
      throw new Error('missing username/password')
    }

    lastChannelsRef.current = channels

    const ws0 = wsRef.current
    if (ws0 && ws0.readyState === WebSocket.OPEN && isLoggedOnRef.current) return

    if (connectPromiseRef.current) {
      await connectPromiseRef.current
      return
    }

    connectPromiseRef.current = (async () => {
      // `lastEvent` is cleared, not carried over: it is the record of how the
      // *previous* attempt ended, and ConnectivityOverlay reads it to decide
      // whether zellowork.io is reachable. Left in place, a fresh attempt after
      // a property switch would be judged on the old property's outcome.
      setState(p => ({ ...p, status: 'connecting', error: undefined, lastEvent: undefined }))
      clearReconnectTimer()

      if (wsRef.current) closeWs()

      const ws = new WebSocket(serverUrl)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws
      // closeWs above set selfDisconnect=true to suppress any stray onclose
      // from the previous socket; reset for the fresh one.
      selfDisconnectRef.current = false

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => resolve()
        // The upgrade itself failed: DNS, TCP 443, TLS or the WebSocket
        // upgrade was refused, which is the shape a firewall allowlist
        // produces. Recorded as state, not just a rejection, for two reasons:
        // without it `status` sits at `connecting` forever on a blocked
        // network (a permanent spinner, with nothing saying why), and
        // ConnectivityOverlay reads this to decide whether zellowork.io is
        // genuinely unreachable.
        //
        // `ws_open_failed` is deliberately distinct from the two events that
        // look similar but mean the opposite: `ws_error` can only fire once
        // the socket is already open, and `logon_error` means the socket
        // reached Zello and our credentials were rejected. Neither is a
        // network problem, and neither should send anyone after their firewall.
        ws.onerror = () => {
          setState(p => ({ ...p, status: 'failed', error: 'ws open failed', lastEvent: 'ws_open_failed' }))
          reject(new Error('ws open failed'))
        }
      })

      ws.onmessage = (ev: MessageEvent) => {
        const data = ev.data

        if (typeof data === 'string') {
          let msg: any
          try { msg = JSON.parse(data) } catch (err) { NOOP(err); return }

          // Per Zello SDK (session.js jsonDataHandler): capture refresh_token
          // from any json message — Zello can rotate it mid-session.
          if (typeof msg.refresh_token === 'string' && msg.refresh_token) {
            refreshTokenRef.current = msg.refresh_token
          }

          if (typeof msg.seq === 'number' && (msg.success !== undefined || msg.error !== undefined)) {
            if (!isLoggedOnRef.current) {
              if (msg.success) {
                isLoggedOnRef.current = true
                reconnectAttemptsRef.current = 0
                setState(p => ({
                  ...p,
                  status: 'connected',
                  error: undefined,
                  lastEvent: 'logon_ok',
                  connectionFailed: false,
                }))
                emit({ type: 'logon_ok' })

                // Probe-only watchdog: nudge Zello with a text-to-self
                // every NO_STATUS_WATCHDOG_MS until on_channel_status
                // arrives or we hit the probe cap. Does NOT reconnect —
                // txAudio's retry loop + forceReconnect handle the harder
                // recovery on actual PTT presses.
                armWatchdog()
              } else {
                const errTxt = String(msg.error || 'logon failed')

                // "channel not found:<name> not found in the contact list" —
                // Zello rejects the WHOLE logon if any one channel is bad
                // (typically a brand-new alert channel where the user-add
                // hasn't propagated yet). Filter out the offending channel
                // and retry instead of dropping the entire session.
                const notFoundMatch = errTxt.match(/channel not found:([^\s]+)/i)
                let badChannel: string | null = notFoundMatch?.[1]?.trim() || null
                if (!badChannel && errTxt.toLowerCase().includes('not found')) {
                  const lc = errTxt.toLowerCase()
                  badChannel =
                    lastChannelsRef.current.find(c => lc.includes(c.toLowerCase())) || null
                }

                if (badChannel) {
                  const filtered = lastChannelsRef.current.filter(
                    c => c.trim().toLowerCase() !== badChannel!.trim().toLowerCase(),
                  )
                  if (filtered.length > 0 && filtered.length < lastChannelsRef.current.length) {
                    console.warn('[Zello] logon rejected channel; retrying without it', {
                      badChannel,
                      remaining: filtered,
                    })
                    isLoggedOnRef.current = false
                    refreshTokenRef.current = null
                    // Tell upstream so it can drop the channel from its
                    // listening list (otherwise the connect-side effect
                    // re-adds it on next render).
                    emit({ type: 'channel_not_found', channel: badChannel })
                    setState(p => ({ ...p, lastEvent: 'channel_not_found' }))
                    // Close the failed WS NOW (instead of leaving it open
                    // while the outer's logon polling waits 8s for a
                    // success that will never come). The polling-loop
                    // wsRef check above will reject promptly, freeing
                    // connectPromiseRef so the retry can open a fresh WS.
                    closeWs()
                    setTimeout(() => {
                      void ensureFnRef.current?.(filtered).catch(NOOP)
                    }, 0)
                    return
                  }
                }

                isLoggedOnRef.current = false
                refreshTokenRef.current = null
                setState(p => ({ ...p, status: 'failed', error: errTxt, lastEvent: 'logon_error' }))
                emit({ type: 'logon_error', error: errTxt })
              }
              return
            }
            // Post-logon seq responses are owned by their command issuer
            // (txAudio for start_stream / stop_stream).
            return
          }

          if (msg.command === 'on_channel_status' && typeof msg.channel === 'string') {
            const key = msg.channel.trim().toLowerCase()
            if (key) {
              if (msg.status === 'online') {
                readyChannelsRef.current.add(key)
                const waiters = channelWaitersRef.current.get(key)
                if (waiters) {
                  waiters.forEach(w => { clearTimeout(w.timer); w.resolve() })
                  channelWaitersRef.current.delete(key)
                }
                setState(p => ({ ...p, readyChannels: Array.from(readyChannelsRef.current) }))
              } else if (msg.status === 'offline') {
                readyChannelsRef.current.delete(key)
                if (msg.error_type === 'configuration' && typeof msg.error === 'string') {
                  const errTxt = `Channel "${msg.channel}" is not available: ${msg.error}`
                  setState(p => ({
                    ...p,
                    error: errTxt,
                    lastEvent: 'channel_configuration_error',
                    readyChannels: Array.from(readyChannelsRef.current),
                  }))
                  const waiters = channelWaitersRef.current.get(key)
                  if (waiters) {
                    waiters.forEach(w => { clearTimeout(w.timer); w.reject(new Error(errTxt)) })
                    channelWaitersRef.current.delete(key)
                  }
                } else {
                  setState(p => ({ ...p, readyChannels: Array.from(readyChannelsRef.current) }))
                }
              }
            }

            // Stop nudging only when EVERY subscribed channel has emitted
            // status. Partial progress (e.g. Everyone online but Housekeeping
            // missing) is not enough — keep probing for the laggards. Probe
            // budget still counts toward MAX_WATCHDOG_RETRIES so we don't
            // probe forever for a channel that may never come online.
            const subscribed = lastChannelsRef.current
              .map(c => c.trim().toLowerCase())
              .filter(Boolean)
            const allReady =
              subscribed.length > 0 && subscribed.every(c => readyChannelsRef.current.has(c))
            if (allReady) {
              clearWatchdog()
              setState(p => (p.connectionFailed ? { ...p, connectionFailed: false } : p))
            }
          }

          onJsonMessage(msg)
          return
        }

        if (data instanceof ArrayBuffer) {
          onBinaryMessage(data)
        }
      }

      ws.onerror = () => setState(p => ({ ...p, status: 'failed', error: 'ws error', lastEvent: 'ws_error' }))

      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null
        isLoggedOnRef.current = false
        readyChannelsRef.current.clear()
        rejectAllWaiters('disconnected')

        let isKicked = false
        setState(p => {
          isKicked = p.status === 'kicked'
          if (isKicked) return { ...p, readyChannels: [] }
          return { ...p, status: 'disconnected', lastEvent: 'ws_close', readyChannels: [] }
        })

        if (selfDisconnectRef.current) {
          selfDisconnectRef.current = false
          return
        }
        if (isKicked) return
        if (!lastChannelsRef.current.length) return

        scheduleReconnect()
      }

      const logon: any = {
        command: 'logon',
        seq: seqRef.current++,
        channels,
        // Per spec §81 only `transcriptions` is documented.
        features: { transcriptions: true },
      }

      // Per Zello SDK doLogon: prefer refresh_token (fast path) over
      // auth_token. Username/password are sent regardless when present.
      if (refreshTokenRef.current) {
        logon.refresh_token = refreshTokenRef.current
      } else if (authToken) {
        logon.auth_token = authToken
      }
      if (username) logon.username = username
      if (password) logon.password = password
      if (language) logon.language = language

      safeSend(logon)

      await new Promise<void>((resolve, reject) => {
        const start = Date.now()
        const timeoutMs = 8000
        const tick = () => {
          if (isLoggedOnRef.current) return resolve()
          // Bail early if our WS got closed underneath us (e.g.
          // channel_not_found path closed it to retry with a filtered set).
          // Otherwise we'd waste the full 8s waiting on a dead socket.
          if (!wsRef.current || wsRef.current !== ws) {
            return reject(new Error('logon aborted (ws closed)'))
          }
          if (Date.now() - start > timeoutMs) return reject(new Error('logon timeout'))
          setTimeout(tick, 50)
        }
        tick()
      })
    })()

    try {
      await connectPromiseRef.current
    } finally {
      connectPromiseRef.current = null
    }
  }, [armWatchdog, authToken, clearReconnectTimer, clearWatchdog, closeWs, emit, language, onBinaryMessage, onJsonMessage, password, rejectAllWaiters, safeSend, scheduleReconnect, serverUrl, setState, username])

  useEffect(() => {
    ensureFnRef.current = ensureConnectedAndLoggedOn
  }, [ensureConnectedAndLoggedOn])

  useEffect(() => {
    return () => {
      clearReconnectTimer()
    }
  }, [clearReconnectTimer])

  return {
    wsRef,
    seqRef,
    isLoggedOnRef,
    readyChannelsRef,
    safeSend,
    safeSendBinary,
    closeWs,
    ensureConnectedAndLoggedOn,
    waitForChannelReady,
  }
}
